'use client';

import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';

import { Locale } from '../../../core/i18n/Locale';
import { TranslationMessages } from '../../../core/i18n/Messages';
import { fetchTranslations } from '../../../core/i18n/Translations';
import { logger } from '../../../core/Log/logger';
import { getAllSitesEnabled, Site } from '../../../core/Site/Site';
import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { Select } from '../../../design-system/components/form/select/Select';
import { Modal } from '../../../design-system/components/modal/Modal';
import { useModal } from '../../../design-system/components/modal/useModal';
import { Separator } from '../../../design-system/components/separator/Separator';
import { useCurrentSite } from '../../providers/currentSite/useCurrentSite';

import style from './SiteSuggestionModal.module.css';
import {
    alternateLinkForLocale,
    getSiteMatchingUserLang,
    setSiteSuggestionModalHasBeenShown,
} from './useSiteSuggestionModal';

export function SiteSuggestionModal() {
    const { trackEvent } = useTrackEventAmplitude();
    const modal = useModal();
    const modalRef = useRef(modal);
    const { currentSite } = useCurrentSite();
    const [match, setMatch] = useState<{
        site: Site;
        messages: TranslationMessages;
    } | null>(null);
    const [selectedLocale, setSelectedLocale] = useState<string>('');
    const otherSites = getAllSitesEnabled().filter((site) => {
        return site.locale !== currentSite.locale;
    });
    const currentSiteLocale = currentSite.locale;

    modalRef.current = modal;

    useEffect(() => {
        const matchingSite = getSiteMatchingUserLang();

        if (matchingSite && matchingSite.locale !== currentSiteLocale) {
            fetchTranslations(matchingSite.locale)
                .then((module) => {
                    setMatch({
                        site: matchingSite,
                        messages: module.default,
                    });
                    setSelectedLocale(matchingSite.locale);
                })
                .then(() => {
                    setSiteSuggestionModalHasBeenShown();
                    modalRef.current.open();
                    trackEvent((ampli, defaultProperties) => {
                        ampli.modalSiteSuggestionDisplayed(defaultProperties);
                    });
                })
                .catch((error: unknown) => {
                    logger.error(
                        'Could not fetch translations of SiteSuggestionModal',
                        { currentSiteLocale, matchingSiteLocale: matchingSite.locale },
                        error,
                    );
                });
        }
    }, [currentSiteLocale, trackEvent]);

    const noMatch = match === null;
    const sameLocale = match?.site.locale === currentSite.locale;

    if (noMatch || sameLocale || selectedLocale === '') {
        return null;
    }

    return (
        <Modal
            {...modal}
            close={() => {
                modal.close();
                trackEvent((ampli, defaultProperties) => {
                    ampli.modalSiteSuggestionClosed(defaultProperties);
                });
            }}
            title={
                <IntlProvider locale={match.site.locale} messages={match.messages}>
                    <FormattedMessage id="global.component.siteSuggestion.title" />
                </IntlProvider>
            }
        >
            <div className={style.recommendedChoices}>
                <IntlProvider locale={match.site.locale} messages={match.messages}>
                    <a
                        className={style.choice}
                        href={alternateLinkForLocale(match.site.locale)}
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.siteSuggestionChanged({
                                    ...defaultProperties,
                                    site_suggested: true,
                                });
                            });
                        }}
                    >
                        <img
                            alt={match.site.hrefLang}
                            className={style.choiceFlag}
                            src={`/static/varanasi/assets/images/flags/4x3/${match.site.country}.svg`}
                        />
                        <FormattedMessage
                            tagName="strong"
                            id="global.component.siteSuggestion.changeWebsite"
                        />
                    </a>
                </IntlProvider>
                <div
                    className={style.choice}
                    onClick={() => {
                        modal.close();
                        trackEvent((ampli, defaultProperties) => {
                            ampli.siteSuggestionStayed(defaultProperties);
                        });
                    }}
                >
                    <img
                        alt={currentSite.hrefLang}
                        className={style.choiceFlag}
                        src={`/static/varanasi/assets/images/flags/4x3/${currentSite.country}.svg`}
                    />
                    <FormattedMessage
                        tagName="strong"
                        id="global.component.siteSuggestion.stayOnWebsite"
                    />
                </div>
            </div>

            <Separator
                className={style.otherChoicesSeparator}
                wording="Or choose an other website"
            />

            <div className={style.selectWithButton}>
                <div className={style.selectWrapper}>
                    <Select
                        label="Evaneos Websites"
                        options={otherSites.map((site) => {
                            return {
                                value: site.locale,
                                label: site.titleWithMarket,
                            };
                        })}
                        setSelected={setSelectedLocale}
                        selected={selectedLocale}
                    />
                </div>
                <div className={style.buttonWrapper}>
                    <Button
                        priority="secondary"
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.siteSuggestionChanged({
                                    ...defaultProperties,
                                    site_suggested: selectedLocale === match.site.locale,
                                });
                            });
                            window.location.assign(
                                alternateLinkForLocale(selectedLocale as Locale),
                            );
                        }}
                    >
                        Go to Website
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
